import { useRef, useState } from "react";
import { router } from "@inertiajs/react";
import Panel from "@/components/ui/Panel";
import Button from "@/components/ui/Button";
import { Input } from "@/components/ui/Field";

// Photos from the day, for HQ's highlight reel. They go to HQ only, not onto
// the public page, so the organizer can send a camera roll without curating it.
export default function RecapPhotos({
  eventId,
  photos,
  limit,
  link,
}: {
  eventId: number;
  photos: string[];
  limit: number;
  link: string | null;
}) {
  const input = useRef<HTMLInputElement>(null);
  const [uploading, setUploading] = useState(false);
  const [album, setAlbum] = useState(link ?? "");
  const [savingLink, setSavingLink] = useState(false);
  const full = photos.length >= limit;

  function upload(files: FileList | null) {
    if (!files || files.length === 0) return;

    setUploading(true);
    router.post(
      `/organize/${eventId}/photos`,
      { photos: Array.from(files) },
      {
        forceFormData: true,
        preserveScroll: true,
        onFinish: () => {
          setUploading(false);
          if (input.current) input.current.value = "";
        },
      },
    );
  }

  return (
    <Panel title="photos.txt">
      <div className="flex flex-col gap-4">
        <div className="flex flex-col gap-1">
          <h2 className="font-display text-heading text-ink font-bold">
            Send us photos from your Boba Bash
          </h2>
          <p className="text-body text-ink-muted">
            We're making a highlight reel of this year. Upload them here, or
            paste a Google Drive or Google Photos link. The first 10 cities to
            send photos get a $5 HCB grant. Only send photos everyone in them is
            happy to have shared.
          </p>
        </div>

        <div className="flex flex-wrap items-center gap-3">
          <input
            ref={input}
            type="file"
            accept="image/png,image/jpeg,image/webp,image/gif"
            multiple
            className="sr-only"
            id="recap-photos"
            onChange={(e) => upload(e.target.files)}
            disabled={uploading || full}
          />
          <Button
            type="button"
            onClick={() => input.current?.click()}
            disabled={uploading || full}
          >
            {uploading ? "Uploading..." : "Upload photos"}
          </Button>
          <span className="text-caption text-ink-soft">
            {photos.length} of {limit} uploaded. PNG, JPEG, WEBP or GIF, up to
            15MB each.
          </span>
        </div>

        <form
          onSubmit={(e) => {
            e.preventDefault();
            setSavingLink(true);
            router.post(
              `/organize/${eventId}/photos/link`,
              { link: album },
              { preserveScroll: true, onFinish: () => setSavingLink(false) },
            );
          }}
          className="flex flex-col gap-2 sm:flex-row"
        >
          <label htmlFor="recap-link" className="sr-only">
            Google Drive or Google Photos link
          </label>
          <Input
            id="recap-link"
            type="url"
            value={album}
            onChange={(e) => setAlbum(e.target.value)}
            placeholder="https://photos.app.goo.gl/..."
            className="flex-1"
          />
          <Button
            type="submit"
            variant="secondary"
            disabled={savingLink || album.trim() === ""}
            className="shrink-0"
          >
            {link ? "Update link" : "Send link"}
          </Button>
        </form>
        <p className="text-caption text-ink-soft -mt-2">
          Set the album or folder so anyone with the link can view it.
        </p>

        {photos.length > 0 && (
          <div className="grid grid-cols-3 gap-2 sm:grid-cols-5">
            {photos.map((src) => (
              <img
                key={src}
                src={src}
                alt=""
                loading="lazy"
                className="border-line aspect-square w-full border object-cover"
              />
            ))}
          </div>
        )}
      </div>
    </Panel>
  );
}
